/**
 * Utility functions for extracting and working with localized notice content
 * Handles ContentJSON structure: { "en": {...}, "hi": {...}, "te": {...}, ... }
 */

/**
 * Extract notice content in the requested language with fallback strategy
 * 
 * @param contentJSON - The ContentJSON string or parsed object from database
 * @param languageCode - The requested language code (e.g., 'en', 'hi', 'te')
 * @returns The localized content object, or null if content cannot be parsed
 * 
 * Fallback strategy:
 * 1. Try requested language
 * 2. Fallback to English ('en')
 * 3. Fallback to first available language
 * 4. Return null if no content available
 */
export const getLocalizedNoticeContent = (
    contentJSON: string | object | null | undefined,
    languageCode: string
): any => {
    // Handle null/undefined
    if (!contentJSON) {
        return null;
    }

    // Parse if string
    let content: any;
    if (typeof contentJSON === 'string') {
        try {
            content = JSON.parse(contentJSON);
        } catch (e) {
            console.error('Failed to parse ContentJSON:', e);
            return null;
        }
    } else {
        content = contentJSON;
    }

    // If content is not an object with language keys, return as-is
    if (typeof content !== 'object' || content === null || Array.isArray(content)) {
        return content;
    }

    // Normalize language code (lowercase)
    const normalizedLang = languageCode.toLowerCase();

    // Try requested language first
    if (content[normalizedLang]) {
        return content[normalizedLang];
    }

    // Fallback to English
    if (content['en']) {
        return content['en'];
    }

    // Fallback to first available language
    const firstKey = Object.keys(content)[0];
    if (firstKey) {
        return content[firstKey];
    }

    // Final fallback
    return null;
};

/**
 * Get all available languages in a ContentJSON object
 * 
 * @param contentJSON - The ContentJSON string or parsed object
 * @returns Array of language codes available in the content
 */
export const getAvailableLanguages = (
    contentJSON: string | object | null | undefined
): string[] => {
    if (!contentJSON) {
        return [];
    }

    let content: any;
    if (typeof contentJSON === 'string') {
        try {
            content = JSON.parse(contentJSON);
        } catch (e) {
            return [];
        }
    } else {
        content = contentJSON;
    }

    if (typeof content !== 'object' || content === null || Array.isArray(content)) {
        return [];
    }

    return Object.keys(content);
};

/**
 * Check if a specific language is available in the content
 * 
 * @param contentJSON - The ContentJSON string or parsed object
 * @param languageCode - The language code to check
 * @returns True if the language is available
 */
export const hasLanguage = (
    contentJSON: string | object | null | undefined,
    languageCode: string
): boolean => {
    const available = getAvailableLanguages(contentJSON);
    return available.includes(languageCode.toLowerCase());
};

